import React, { useState, useMemo, useEffect, useCallback } from "react";

const createDate = (year, month) => {
    const date = new Date(year, month, 0);
    const firstDay = new Date(year, month).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const prettyName = date.toLocaleDateString("en-US", {
        timeZone: "UTC",
        month: "long",
    });

    return {
        firstDay,
        totalDays,
        prettyName,
    };
};

const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

export const CalendarView = ({
    startDate = new Date(),
    specialDate1,
    specialDate2,
}) => {
    const dateProp = useMemo(() => new Date(startDate), [startDate]);
    const [selectedMonth, setSelectedMonth] = useState(dateProp.getMonth());
    const [selectedYear, setSelectedYear] = useState(dateProp.getFullYear());
    const [days, setDays] = useState([]);
    const date = createDate(selectedYear, selectedMonth);

    const setDate = useCallback(() => {
        const newDays = [...Array(date.totalDays).keys()];
        setDays(newDays);
    }, [date.totalDays]);

    useEffect(() => setDate(), [setDate, selectedMonth, selectedYear]);

    const changeMonth = (change) => {
        const isJanuary = selectedMonth === 0;
        const isDecember = selectedMonth === months.length - 1;

        if (isJanuary && change < 0) {
            setSelectedYear((y) => y - 1);
            setSelectedMonth(months.length - 1);
        } else if (isDecember && change > 0) {
            setSelectedYear((y) => y + 1);
            setSelectedMonth(0);
        } else {
            setSelectedMonth((m) => m + change);
        }
    };

    // helper function to check if two dates are the same day
    const isSameDay = (d1, d2) => {
        return (
            d1 &&
            d2 &&
            d1.getDate() === d2.getDate() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getFullYear() === d2.getFullYear()
        );
    };

    return (
        <div className="w-full" dir="ltr">
            {/* Header */}
            <div className="flex justify-between items-center py-2 px-1">
                <button
                    onClick={() => changeMonth(-1)}
                    className="group text-xl w-8 h-8 flex justify-center items-center hover:bg-[#006E7E] hover:text-white rounded-full transition"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="6"
                        height="12"
                        fill="none"
                        viewBox="0 0 6 12"
                        className="group-hover:invert "
                    >
                        <path
                            stroke="#4B4B4B"
                            strokeLinecap="round"
                            strokeWidth="1.5"
                            d="M5.25.75s-4.5 4-4.5 5 4.5 5.5 4.5 5.5"
                        ></path>
                    </svg>
                </button>
                <div className="text-base font-semibold">
                    {months[selectedMonth]} {selectedYear}
                </div>
                <button
                    onClick={() => changeMonth(1)}
                    className="group text-xl w-8 h-8  hover:bg-[#006E7E] flex justify-center items-center hover:text-white rounded-full transition"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="6"
                        height="12"
                        fill="none"
                        viewBox="0 0 6 12"
                        className="group-hover:invert "
                    >
                        <path
                            stroke="#4B4B4B"
                            strokeLinecap="round"
                            strokeWidth="1.5"
                            d="M.75.75s4.5 4 4.5 5-4.5 5.5-4.5 5.5"
                        ></path>
                    </svg>
                </button>
            </div>

            {/* Weekdays */}
            <div className="grid grid-cols-7 gap-1 mb-2">
                {daysOfWeek.map((dow) => (
                    <div key={dow} className="text-center font-medium">
                        {dow}
                    </div>
                ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                    const gridStart = day === 0 ? { gridColumnStart: date.firstDay + 1 } : {};
                    const currentDate = new Date(selectedYear, selectedMonth, day + 1);

                    const isStart = isSameDay(currentDate, startDate);
                    const isSpecial1 = isSameDay(currentDate, specialDate1);
                    const isSpecial2 = isSameDay(currentDate, specialDate2);

                    return (
                        <div
                            key={day}
                            style={gridStart}
                            className={`relative flex items-center justify-center w-8 h-8 rounded-full transition text-sm
                ${isStart
                                    ? "bg-[#006E7E] text-white"
                                    : ""
                                }`}
                        >
                            {/* Dots */}
                            {(isSpecial1 || isSpecial2) && (
                                <div className="absolute top-0 flex gap-[2px] justify-center mt-[2px]">
                                    {isSpecial1 && (
                                        <span
                                            className="block w-[4px] h-[4px] rounded-full"
                                            style={{ backgroundColor: "#F9C416" }}
                                        />
                                    )}
                                    {isSpecial2 && (
                                        <>
                                            <span
                                                className="block w-[4px] h-[4px] rounded-full"
                                                style={{ backgroundColor: "#F9C416" }}
                                            />
                                            <span
                                                className="block w-[4px] h-[4px] rounded-full"
                                                style={{ backgroundColor: "#006E7E" }}
                                            />
                                        </>
                                    )}
                                </div>
                            )}

                            {day + 1}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
